/**
 * Labels — board-owned labels (`labels.project_id NOT NULL`).
 *
 * The workspace-scoped list is an AGGREGATION, not a second home for labels:
 * it shows the labels of every board the caller may read, each labelled with
 * its owning board. Every mutation still targets the board that owns the
 * label (`POST /projects/:pId/labels`, `PATCH /labels/:lId`,
 * `DELETE /labels/:lId`), so a label never leaves its board.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface WorkspaceLabel {
  id: string
  projectId: string
  name: string
  color: string
  version: number
  archivedAt: string | null
  /** The board that owns the label. */
  projectKey: string
  projectName: string
  /** How many non-archived tasks carry the label. */
  taskCount: number
}

export interface WorkspaceLabelBoard {
  id: string
  key: string
  name: string
  archivedAt: string | null
  /** Whether the caller may write to this board. */
  canWrite: boolean
}

export interface WorkspaceLabelsPayload {
  labels: WorkspaceLabel[]
  boards: WorkspaceLabelBoard[]
  total: number
  limit: number
  offset: number
}

export interface WorkspaceLabelsQuery {
  q?: string
  projectId?: string
  includeArchived?: boolean
  limit?: number
  offset?: number
}

/** Every label of every board the caller may read. */
export function useWorkspaceLabels(wsId: string | undefined, params: WorkspaceLabelsQuery = {}) {
  const { q, projectId, includeArchived, limit, offset } = params
  return useQuery({
    queryKey: [
      'workspace-labels',
      wsId,
      {
        q: q ?? '',
        projectId: projectId ?? '',
        includeArchived: !!includeArchived,
        limit: limit ?? 100,
        offset: offset ?? 0,
      },
    ],
    queryFn: () =>
      api<WorkspaceLabelsPayload>(`/api/v1/workspaces/${wsId}/labels`, {
        query: {
          q: q || undefined,
          projectId: projectId || undefined,
          includeArchived: includeArchived ? 'true' : undefined,
          limit: limit ?? 100,
          offset: offset ?? 0,
        },
      }),
    enabled: !!wsId,
  })
}

/** Create a label on a board. */
export function useCreateLabel(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; color: string }) =>
      api(`/api/v1/projects/${pId}/labels`, {
        method: 'POST',
        body: input,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-labels', wsId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
    },
  })
}

/** Rename or recolour a label. */
export function useUpdateLabel(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { labelId: string; patch: { name?: string; color?: string } }) =>
      api(`/api/v1/labels/${input.labelId}`, {
        method: 'PATCH',
        body: input.patch,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-labels', wsId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
    },
  })
}

/** Archive a label (soft delete — the server sets `archivedAt`). */
export function useArchiveLabel(wsId: string | undefined, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (labelId: string) =>
      api(`/api/v1/labels/${labelId}`, {
        method: 'DELETE',
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-labels', wsId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
    },
  })
}
